import { type Dir, niceScale } from "../demand/demandMath.ts";
import { positionAt } from "./simulate.ts";
import { type LeftBehindEvent, type SimResult, type TripResult, stopDirIndex } from "./types.ts";

/**
 * What the day replay reads off a simulation result for one instant, kept
 * free of React so it can be tested on its own (replayMath.test.ts). Every
 * function here is called once per animation frame, so each is a scan of
 * small arrays and nothing more.
 */

/** A bus on the road at the replay's clock time. */
export interface BusAt {
  trip: TripResult;
  dir: Dir;
  /** 0 at the line's first stop, 1 at its last, whichever way the bus is going. */
  along: number;
  /** Riders aboard on the link it is on. */
  load: number;
  /** Its next call, or null once it has reached its last stop. */
  next: { stop: number; timeMin: number } | null;
  /** Riders it left behind within the last couple of minutes; 0 when none. */
  leftBehind: number;
}

/** First index whose event time is at or after t (events are time-ordered). */
function lowerBound(events: LeftBehindEvent[], t: number): number {
  let lo = 0;
  let hi = events.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (events[mid].timeMin < t) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** The events that happened in [from, to]. */
export function eventsBetween(events: LeftBehindEvent[], from: number, to: number): LeftBehindEvent[] {
  const out: LeftBehindEvent[] = [];
  for (let i = lowerBound(events, from); i < events.length && events[i].timeMin <= to; i++) out.push(events[i]);
  return out;
}

/** The first event strictly after t, or null. */
export function nextEventAfter(events: LeftBehindEvent[], t: number): LeftBehindEvent | null {
  const i = lowerBound(events, t + 1e-6);
  return i < events.length ? events[i] : null;
}

/** Running total of riders left behind, so "so far today" is one lookup. */
export function leftBehindPrefix(events: LeftBehindEvent[]): number[] {
  const out = new Array<number>(events.length + 1);
  out[0] = 0;
  events.forEach((e, i) => { out[i + 1] = out[i] + e.riders; });
  return out;
}

/** Riders left behind up to and including t, given the prefix sums. */
export function leftBehindUpTo(events: LeftBehindEvent[], prefix: number[], t: number): number {
  return prefix[lowerBound(events, t + 1e-6)] ?? 0;
}

/** Riders left behind at one stop, both directions, up to t. Hover-only: a plain scan. */
export function leftBehindAtStopUpTo(events: LeftBehindEvent[], stop: number, t: number): number {
  let sum = 0;
  for (let i = 0; i < events.length && events[i].timeMin <= t + 1e-6; i++) {
    if (events[i].stop === stop) sum += events[i].riders;
  }
  return sum;
}

/** Riders waiting at a stop, going one way, at the clock time. */
export function waitingAt(result: SimResult, stop: number, dir: Dir, t: number): number {
  const sd = result.stopDirs[stopDirIndex(stop, dir)];
  if (!sd) return 0;
  const w = sd.waiting;
  if (w.length === 0) return 0;
  const m = Math.min(w.length - 1, Math.max(0, Math.floor(t)));
  return w[m];
}

/** Everyone waiting anywhere on the line at the clock time. */
export function totalWaitingAt(result: SimResult, t: number): number {
  let sum = 0;
  for (const sd of result.stopDirs) {
    const w = sd.waiting;
    if (w.length === 0) continue;
    sum += w[Math.min(w.length - 1, Math.max(0, Math.floor(t)))];
  }
  return sum;
}

/** The buses on the road at t, with where they are and what they carry. */
export function busesAt(result: SimResult, t: number, recentMin = 2): BusAt[] {
  const n = result.input.stops.length;
  const recent = eventsBetween(result.events, t - recentMin, t);
  const out: BusAt[] = [];
  for (const trip of result.trips) {
    const calls = trip.trip.calls;
    if (calls.length === 0 || t < calls[0].timeMin || t > calls[calls.length - 1].timeMin) continue;
    const pos = positionAt(trip.trip, t);
    if (!pos) continue;
    const a = calls[pos.from].stop;
    const b = calls[pos.to].stop;
    const along = n > 1 ? (a + (b - a) * pos.frac) / (n - 1) : 0;
    let next: BusAt["next"] = null;
    for (const c of calls) {
      if (c.timeMin > t + 1e-6) { next = { stop: c.stop, timeMin: c.timeMin }; break; }
    }
    let leftBehind = 0;
    for (const e of recent) if (e.tripId === trip.trip.id) leftBehind += e.riders;
    out.push({
      trip,
      dir: trip.trip.dir,
      along,
      load: pos.from === pos.to ? 0 : (trip.linkLoads[pos.from] ?? 0),
      next,
      leftBehind,
    });
  }
  return out;
}

/** The span every lane is drawn and scrubbed over: the earliest first minute to the latest last. */
export function laneSpan(results: SimResult[]): [number, number] {
  let from = Infinity;
  let to = -Infinity;
  for (const r of results) {
    from = Math.min(from, r.totals.firstMin);
    to = Math.max(to, r.totals.lastMin);
  }
  if (!Number.isFinite(from)) return [0, 0];
  return [from, Math.max(from, to)];
}

/** When the first bus of the day leaves, for the clock to start on. */
export function firstDeparture(results: SimResult[]): number | null {
  let first = Infinity;
  for (const r of results) {
    for (const trip of r.trips) {
      if (trip.trip.calls.length > 0) first = Math.min(first, trip.trip.calls[0].timeMin);
    }
  }
  return Number.isFinite(first) ? first : null;
}

/**
 * The top of the queue-bar scale, shared by every lane so a bar means the
 * same riders wherever it is drawn: a round number just above the largest
 * queue any stop ever had while a bus was still to come.
 */
export function queueScaleMax(results: SimResult[]): number {
  let max = 0;
  for (const r of results) for (const sd of r.stopDirs) max = Math.max(max, sd.maxWaiting);
  return max > 0 ? niceScale(max).max : 10;
}

/** The minute with the most riders waiting on the line, over all runs. */
export function busiestMinute(results: SimResult[]): number | null {
  let bestMinute: number | null = null;
  let best = -1;
  for (const r of results) {
    const minutes = r.stopDirs[0]?.waiting.length ?? 0;
    for (let m = 0; m < minutes; m++) {
      let sum = 0;
      for (const sd of r.stopDirs) sum += sd.waiting[m];
      if (sum > best) { best = sum; bestMinute = m; }
    }
  }
  return best > 0 ? bestMinute : null;
}
